/**
 * The navigation icons, as one stroked set.
 *
 * These were Unicode characters — `⌖ ⬢ ✓ ! ⚠ ▦ ⇄ ⚒ ◷ ☰ ▤ ⚙` — picked one at a
 * time for meaning, which is the right way to pick them and the wrong way to
 * draw them. A glyph set assembled from four Unicode blocks has four different
 * stroke weights, four optical sizes and four baselines, and several of them
 * fall back to whatever the device happens to have: `⚒` and `⬢` are emoji on
 * most phones, so two rows of the sidebar rendered in colour. The nav is the
 * one component on screen at all times, so it is the one that most has to look
 * like it was drawn by one hand.
 *
 * Hand-written SVG rather than an icon package: twelve icons is about 2 KB
 * here, against ~50 KB for a library of a thousand, and this is downloaded over
 * workshop wifi on a phone (CLAUDE.md is explicit that every kilobyte is). They
 * inherit `currentColor` and the 1.6 stroke, so the active/inactive colour
 * change in `app.css` is the only thing that moves.
 *
 * Drawn on a 24-grid and rendered at 16 or 19, with `vector-effect` left off
 * deliberately — at these sizes a scaled stroke is what keeps the set looking
 * even between the phone bar and the sidebar.
 */

const PATHS: Readonly<Record<string, string>> = {
  // A clock face: Today is where the clock is.
  today: 'M12 20.5a8.5 8.5 0 100-17 8.5 8.5 0 000 17zM12 7.5V12l3 2',
  // A calendar with a mark on one day: your shifts.
  hours: 'M4.5 7.5A1.5 1.5 0 016 6h12a1.5 1.5 0 011.5 1.5v11A1.5 1.5 0 0118 20H6a1.5 1.5 0 01-1.5-1.5zM4.5 10.5h15M8.5 4v4M15.5 4v4M8.5 14h3v3h-3z',
  // Two people: the office is the team and its hours.
  office: 'M9 11a3 3 0 100-6 3 3 0 000 6zM3.5 19.5a5.5 5.5 0 0111 0M15.5 5.2a3 3 0 010 5.6M17 14.8a5.5 5.5 0 013.5 4.7',
  more: 'M4.5 7h15M4.5 12h15M4.5 17h15',
  // A box with an arrow going in: a delivery being taken in.
  receive: 'M4.5 10.5v8A1.5 1.5 0 006 20h12a1.5 1.5 0 001.5-1.5v-8M3.5 10.5h17M12 3.5v9M8.5 9L12 12.5 15.5 9',
  signout: 'M14 4.5h3.5A1.5 1.5 0 0119 6v12a1.5 1.5 0 01-1.5 1.5H14M9.5 8L5.5 12l4 4M5.5 12H15',
  // A reticle over a target: point the phone at the label.
  scan: 'M4 8V5.5A1.5 1.5 0 015.5 4H8M16 4h2.5A1.5 1.5 0 0120 5.5V8M20 16v2.5a1.5 1.5 0 01-1.5 1.5H16M8 20H5.5A1.5 1.5 0 014 18.5V16M12 9.5v5M9.5 12h5',
  // A robot on its wheels, seen head on — the fleet is the thing itself.
  fleet: 'M9 4h6M12 4v3M6.5 7h11A1.5 1.5 0 0119 8.5v6a1.5 1.5 0 01-1.5 1.5h-11A1.5 1.5 0 015 14.5v-6A1.5 1.5 0 016.5 7zM9.5 10.5v1.5M14.5 10.5v1.5M8 16v2M16 16v2',
  // A checklist: work is a list of things that get done.
  work: 'M4.5 6.5l1.5 1.5 2.5-2.5M4.5 12.5l1.5 1.5 2.5-2.5M4.5 18.5L6 20l2.5-2.5M12 6.5h7.5M12 12.5h7.5M12 18.5h7.5',
  // A speech mark with a mark in it: somebody said something about a robot.
  squawks: 'M20 13.5a2.5 2.5 0 01-2.5 2.5H10l-4 3.5V16H6.5A2.5 2.5 0 014 13.5v-7A2.5 2.5 0 016.5 4h11A2.5 2.5 0 0120 6.5zM12 7.5v3.5M12 13.2v.1',
  // The warning triangle, drawn to the same weight as everything else.
  failures: 'M10.7 4.6L3.4 17.2A1.5 1.5 0 004.7 19.5h14.6a1.5 1.5 0 001.3-2.3L13.3 4.6a1.5 1.5 0 00-2.6 0zM12 9.5v4M12 16.3v.1',
  // Boxes on a shelf.
  stock: 'M4 9.5h16M4 14.5h16M4 5.5h16v13H4zM9.5 5.5v13M14.5 5.5v13',
  // Two arrows passing: what is coming in and what is going out.
  orders: 'M4 8.5h13M13.5 5l3.5 3.5-3.5 3.5M20 15.5H7M10.5 12L7 15.5l3.5 3.5',
  // A part going into an assembly.
  builds: 'M12 3.5l7.5 4.2v8.6L12 20.5 4.5 16.3V7.7zM12 12l7.5-4.3M12 12v8.5M12 12L4.5 7.7',
  // A calendar: planning is about dates before it is about parts.
  plan: 'M4.5 7.5A1.5 1.5 0 016 6h12a1.5 1.5 0 011.5 1.5v11A1.5 1.5 0 0118 20H6a1.5 1.5 0 01-1.5-1.5zM4.5 10.5h15M8.5 4v4M15.5 4v4',
  // A tree: the catalog is the shape of the robot.
  catalog: 'M6 5h5M6 5v14h5M6 12h5M13.5 3.5h5v3h-5zM13.5 10.5h5v3h-5zM13.5 17.5h5v3h-5z',
  // Bars: a report is a comparison.
  reports: 'M4.5 19.5h15M7.5 19.5v-6M12 19.5V6.5M16.5 19.5v-9',
  // The gear, and only the gear.
  settings:
    'M12 15a3 3 0 100-6 3 3 0 000 6zM19.1 14a1.5 1.5 0 00.3 1.7l.1.1a1.8 1.8 0 11-2.6 2.6l-.1-.1a1.5 1.5 0 00-1.7-.3 1.5 1.5 0 00-.9 1.4v.2a1.8 1.8 0 11-3.6 0v-.1a1.5 1.5 0 00-1-1.4 1.5 1.5 0 00-1.7.3l-.1.1a1.8 1.8 0 11-2.6-2.6l.1-.1a1.5 1.5 0 00.3-1.7 1.5 1.5 0 00-1.4-.9h-.2a1.8 1.8 0 110-3.6h.1a1.5 1.5 0 001.4-1 1.5 1.5 0 00-.3-1.7l-.1-.1a1.8 1.8 0 112.6-2.6l.1.1a1.5 1.5 0 001.7.3H10a1.5 1.5 0 00.9-1.4v-.2a1.8 1.8 0 113.6 0v.1a1.5 1.5 0 00.9 1.4 1.5 1.5 0 001.7-.3l.1-.1a1.8 1.8 0 112.6 2.6l-.1.1a1.5 1.5 0 00-.3 1.7v.1a1.5 1.5 0 001.4.9h.2a1.8 1.8 0 110 3.6h-.1a1.5 1.5 0 00-1.4.9z',
}

/** A fallback, so an unknown id is a dot rather than a hole in the row. */
const FALLBACK = 'M12 12v.01'

export function Icon({ name, size = 16 }: { name: string; size?: number }): JSX.Element {
  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[name] ?? FALLBACK} />
    </svg>
  )
}
